import { alpha, createTheme } from '@mui/material/styles';
// Softer, tapered shadow scale. Indices 7-24 collapsed to a single moderate
// elevation rather than the previous repeated heavy 0.25-alpha drop. Scale is
// theme-independent — themes that need a more dramatic max shadow can use
// tokens.effect.shadow on specific elevated surfaces.
const shadows = [
    'none',
    '0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)',
    '0 4px 6px -1px rgb(0 0 0 / 0.12), 0 2px 4px -2px rgb(0 0 0 / 0.08)',
    '0 6px 12px -2px rgb(0 0 0 / 0.12), 0 3px 6px -3px rgb(0 0 0 / 0.1)',
    '0 12px 20px -4px rgb(0 0 0 / 0.15), 0 4px 8px -4px rgb(0 0 0 / 0.1)',
    '0 20px 30px -6px rgb(0 0 0 / 0.18), 0 8px 12px -6px rgb(0 0 0 / 0.12)',
    '0 25px 50px -12px rgb(0 0 0 / 0.18)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
    '0 25px 50px -12px rgb(0 0 0 / 0.2)',
];
// Many existing components reference MUI's grey scale (grey.50, grey.100, ...)
// for neutral backgrounds and dividers. MUI's static default is a light-mode
// scale, so on dark themes a `bgcolor: 'grey.100'` resolves to ~#f5f5f5 — a
// white blob. We invert the scale on dark themes so callers don't have to
// rewrite every grey.* reference.
function deriveGreyScale(paletteMode) {
    return paletteMode === 'dark'
        ? {
            50: '#0b1120',
            100: '#111827',
            200: '#1f2937',
            300: '#334155',
            400: '#475569',
            500: '#64748b',
            600: '#94a3b8',
            700: '#cbd5e1',
            800: '#e2e8f0',
            900: '#f8fafc',
            A100: '#1f2937',
            A200: '#334155',
            A400: '#94a3b8',
            A700: '#e2e8f0',
        }
        : {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
            A100: '#f1f5f9',
            A200: '#e2e8f0',
            A400: '#94a3b8',
            A700: '#1e293b',
        };
}
export const createAppTheme = (def, muiLocale) => {
    const { tokens, paletteMode } = def;
    const grey = deriveGreyScale(paletteMode);
    const inputValueColor = paletteMode === 'dark' ? '#ffffff' : '#000000';
    const options = {
        palette: {
            mode: paletteMode,
            primary: {
                main: tokens.action.primary,
                light: tokens.action.primaryHover,
                dark: tokens.action.primary,
                contrastText: tokens.text.inverse,
            },
            secondary: {
                main: tokens.action.secondary,
                contrastText: tokens.text.inverse,
            },
            success: { main: tokens.state.success, contrastText: tokens.text.inverse },
            warning: { main: tokens.state.warning, contrastText: tokens.text.inverse },
            error: { main: tokens.state.error, contrastText: '#ffffff' },
            info: { main: tokens.state.info, contrastText: tokens.text.inverse },
            grey,
            background: {
                default: tokens.app.bg,
                paper: tokens.surface.panel,
            },
            text: {
                primary: tokens.text.primary,
                secondary: tokens.text.secondary,
                disabled: tokens.text.disabled,
            },
            divider: tokens.border.default,
        },
        typography: {
            // Roboto is the corporate typeface — the brand guidelines name it for
            // "printed and digital corporate marketing productions" (Corporate Brand
            // Guidelines p.28). Headings carry the brand's -20 InDesign tracking,
            // which is -0.02em.
            fontFamily: "'Roboto', system-ui, -apple-system, sans-serif",
            h1: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.02em' },
            h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.02em' },
            h3: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '-0.02em' },
            h4: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '-0.02em' },
            body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.55 },
            body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
            caption: { fontSize: '0.8125rem', lineHeight: 1.4 },
            button: { textTransform: 'none', fontWeight: 500 },
        },
        shape: { borderRadius: 8 },
        shadows: shadows,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: tokens.app.bg,
                        color: tokens.text.primary,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        padding: '0.5rem 1.25rem',
                        fontWeight: 600,
                        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                    },
                    containedPrimary: {
                        backgroundColor: tokens.action.primary,
                        color: tokens.text.inverse,
                        boxShadow: `0 0 22px ${tokens.effect.glow}`,
                        '&:hover': {
                            backgroundColor: tokens.action.primaryHover,
                            boxShadow: `0 0 28px ${tokens.effect.glow}`,
                        },
                    },
                    containedSecondary: {
                        backgroundColor: tokens.action.secondary,
                        color: tokens.text.inverse,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: tokens.action.secondary,
                            filter: 'brightness(1.1)',
                            boxShadow: 'none',
                        },
                    },
                    outlined: {
                        borderWidth: '1px',
                        borderColor: tokens.border.default,
                        color: tokens.text.primary,
                        '&:hover': {
                            borderWidth: '1px',
                            borderColor: tokens.border.strong,
                            backgroundColor: alpha(tokens.action.primary, 0.12),
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: tokens.surface.panel,
                        backgroundImage: 'none',
                        boxShadow: tokens.effect.shadow,
                        borderRadius: 16,
                        border: `1px solid ${tokens.border.default}`,
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                            boxShadow: tokens.effect.shadow,
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: tokens.surface.panel,
                        border: `1px solid ${tokens.border.default}`,
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: tokens.surface.panelAlt,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    head: {
                        fontWeight: 700,
                        backgroundColor: tokens.surface.panelAlt,
                        color: tokens.text.primary,
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        borderBottom: `1px solid ${tokens.border.default}`,
                    },
                    body: {
                        fontSize: '0.9375rem',
                        color: tokens.text.primary,
                        borderBottom: `1px solid ${tokens.border.default}`,
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: `${alpha(tokens.action.primary, 0.14)} !important`,
                            boxShadow: `inset 4px 0 0 ${tokens.action.primary}`,
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        // MUI paints a default-colour chip's icon with grey[300] on dark
                        // themes, assuming its stock scale where 300 is a light foreground.
                        // deriveGreyScale inverts the scale, so 300 resolves to #334155 — a
                        // background tone — and the icon lands at ~1.8:1 against the page.
                        // Scoped to colorDefault: MUI already sets coloured chips' icons to
                        // `inherit`, and overriding the bare `icon` slot would clobber that.
                        '&.MuiChip-colorDefault .MuiChip-icon': {
                            color: tokens.text.secondary,
                        },
                    },
                    colorSuccess: {
                        backgroundColor: tokens.state.success,
                        color: tokens.text.inverse,
                    },
                    colorWarning: {
                        backgroundColor: tokens.state.warning,
                        color: tokens.text.inverse,
                    },
                    colorError: {
                        backgroundColor: tokens.state.error,
                        color: '#ffffff',
                    },
                    colorInfo: {
                        backgroundColor: tokens.state.info,
                        color: tokens.text.inverse,
                    },
                },
            },
            // A field's value must be the brightest text in it, never dimmer than its
            // label (#883). Pure white on dark, pure black on light; body text keeps
            // the theme's tint. Disabled is included on purpose: product detail
            // renders every field `disabled` in view mode, and MUI's default
            // text.disabled fill made saved data read like placeholders. Disabled
            // fields stay distinguishable by their muted border.
            // `.MuiInputBase-input` also covers Select's value and Autocomplete's input.
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        color: inputValueColor,
                        '&.Mui-disabled': { color: inputValueColor, WebkitTextFillColor: inputValueColor },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 8,
                            backgroundColor: tokens.surface.input,
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: tokens.surface.input,
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    outlined: {
                        backgroundColor: tokens.surface.input,
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: { borderRadius: 12 },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    // A pop-out (temporary) drawer sits at zIndex.drawer by default, but the
                    // app's top bar is drawer + 1 so it clears the permanent side menu, which
                    // hid every pop-out drawer's first 56px under the bar (#1261). `modal`
                    // reaches temporary drawers only; the docked side menu is unaffected.
                    modal: ({ theme }) => ({ zIndex: theme.zIndex.modal }),
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        borderBottom: `1px solid ${tokens.border.default}`,
                        backgroundColor: tokens.surface.nav,
                        color: tokens.text.primary,
                    },
                },
            },
            MuiTooltip: {
                defaultProps: {
                    arrow: true,
                    enterDelay: 200,
                    enterNextDelay: 100,
                },
                styleOverrides: {
                    tooltip: {
                        backgroundColor: tokens.surface.panelAlt,
                        color: tokens.text.primary,
                        border: `1px solid ${tokens.border.default}`,
                        borderRadius: 8,
                        padding: '0.4rem 0.65rem',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        lineHeight: 1.4,
                        boxShadow: tokens.effect.shadow,
                        maxWidth: 320,
                    },
                    arrow: {
                        color: tokens.surface.panelAlt,
                        '&::before': {
                            border: `1px solid ${tokens.border.default}`,
                            backgroundColor: tokens.surface.panelAlt,
                            boxSizing: 'border-box',
                        },
                    },
                },
            },
        },
    };
    return muiLocale ? createTheme(options, muiLocale) : createTheme(options);
};
// Token names used when writing CSS custom properties onto :root.
// Keep in sync with createAppTheme — the picker UI and ad-hoc styled code
// reference these via var(--token-name).
export function tokensToCssVars(def) {
    const t = def.tokens;
    return {
        '--app-bg': t.app.bg,
        '--app-bg-elevated': t.app.bgElevated,
        '--surface-nav': t.surface.nav,
        '--surface-panel': t.surface.panel,
        '--surface-panel-alt': t.surface.panelAlt,
        '--surface-input': t.surface.input,
        '--border-default': t.border.default,
        '--border-strong': t.border.strong,
        '--text-primary': t.text.primary,
        '--text-secondary': t.text.secondary,
        '--text-disabled': t.text.disabled,
        '--text-inverse': t.text.inverse,
        '--action-primary': t.action.primary,
        '--action-primary-hover': t.action.primaryHover,
        '--action-secondary': t.action.secondary,
        '--state-success': t.state.success,
        '--state-warning': t.state.warning,
        '--state-error': t.state.error,
        '--state-info': t.state.info,
        '--data-series-a': t.data.seriesA,
        '--data-series-b': t.data.seriesB,
        '--data-series-c': t.data.seriesC,
        '--data-series-d': t.data.seriesD,
        '--effect-glow': t.effect.glow,
        '--effect-shadow': t.effect.shadow,
    };
}
//# sourceMappingURL=createAppTheme.js.map